import React, { useState, useEffect } from 'react';
import { holidaysApi, officesApi } from '../lib/api';
import { CalendarDays, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const HolidaysPage = () => {
  const [holidays, setHolidays] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', from_date: '', to_date: '', repayment_rescheduled_to: '', office_id: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [hRes, oRes] = await Promise.all([holidaysApi.list(), officesApi.list()]);
      setHolidays(hRes.data);
      setOffices(oRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    try {
      await holidaysApi.create(form);
      toast.success('Holiday created');
      setShowModal(false);
      setForm({ name: '', from_date: '', to_date: '', repayment_rescheduled_to: '', office_id: '' });
      load();
    } catch (e) { toast.error('Failed to create holiday'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    try { await holidaysApi.delete(id); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const getOfficeName = (id) => offices.find(o => o.id === id)?.name || 'All Offices';

  return (
    <div className="space-y-6 fade-in" data-testid="holidays-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Admin</span><span>/</span><span>Organization</span><span>/</span><span className="text-[#1f1f1f] font-medium">Holidays</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">CALENDAR</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Holidays</h1>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="add-holiday-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Holiday
        </Button>
      </div>

      <div className="bg-white border border-gray-200">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Holiday Name</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">From</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">To</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Office</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            holidays.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-gray-400">No holidays defined. Click "Add Holiday" to create one.</td></tr> :
            holidays.map(h => (
              <tr key={h.id} className="border-b hover:bg-gray-50" data-testid={`holiday-row-${h.id}`}>
                <td className="px-4 py-3 font-medium text-sm text-[#1f1f1f]">{h.name}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{h.from_date}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{h.to_date}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{getOfficeName(h.office_id)}</td>
                <td className="px-4 py-3"><Button variant="ghost" size="sm" onClick={() => handleDelete(h.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" aria-describedby="holiday-form-desc">
          <DialogHeader><DialogTitle>Add Holiday</DialogTitle></DialogHeader>
          <p id="holiday-form-desc" className="sr-only">Form to add a holiday</p>
          <div className="space-y-4">
            <div><Label>Holiday Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="holiday-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>From Date *</Label><Input type="date" value={form.from_date} onChange={e => setForm({...form, from_date: e.target.value})} data-testid="holiday-from" /></div>
              <div><Label>To Date *</Label><Input type="date" value={form.to_date} onChange={e => setForm({...form, to_date: e.target.value})} data-testid="holiday-to" /></div>
            </div>
            <div><Label>Repayments Rescheduled To</Label><Input type="date" value={form.repayment_rescheduled_to} onChange={e => setForm({...form, repayment_rescheduled_to: e.target.value})} data-testid="holiday-reschedule" /></div>
            <div><Label>Office</Label>
              <Select value={form.office_id} onValueChange={v => setForm({...form, office_id: v})}>
                <SelectTrigger data-testid="holiday-office"><SelectValue placeholder="All Offices" /></SelectTrigger>
                <SelectContent>{offices.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="holiday-submit">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HolidaysPage;
